import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import { BRAND } from "@/lib/brand";
import { resolveAssetUrl } from "@/lib/api";
import { ChevronRight } from "lucide-react";

const Welcome = () => {
  return (
    <div className="overflow-x-hidden" data-testid="welcome-page">
      <Header />
      <CartDrawer />

      {/* Page Hero */}
      <section className="border-b border-zinc-900 bg-gradient-to-b from-black via-zinc-950 to-black py-16">
        <div className="max-w-5xl mx-auto px-6 text-center space-y-4">
          <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500">
            // A Letter From The Founder
          </div>
          <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tight leading-[0.95]">
            Welcome to <br />
            <span className="metallic-text">Faith Athletic</span>
          </h1>
          <p className="text-zinc-400 text-sm md:text-base tracking-wide max-w-2xl mx-auto">
            Built on faith. Driven by purpose. A movement — not just a brand.
          </p>
        </div>
      </section>

      {/* Founder Letter */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Founder portrait */}
          <div className="lg:col-span-2 lg:sticky lg:top-28">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-zinc-700/40 via-transparent to-zinc-500/30 blur-2xl"></div>
              <img
                src={resolveAssetUrl(BRAND.FOUNDER)}
                alt="Michael Lewis — Founder, Faith Athletic Clothing"
                className="relative w-full border border-zinc-800 shadow-2xl"
                data-testid="founder-image"
              />
            </div>
            <div className="mt-5 space-y-1 pl-1">
              <div className="font-heading text-2xl font-bold uppercase tracking-tight">
                Michael Lewis
              </div>
              <div className="text-xs text-zinc-400 tracking-widest uppercase">
                Founder · Faith Athletic Clothing
              </div>
              <div className="text-[10px] text-zinc-600 tracking-[0.3em] uppercase pt-2">
                EST @2027
              </div>
            </div>
          </div>

          {/* Letter copy */}
          <article
            className="lg:col-span-3 space-y-6 text-zinc-200 leading-relaxed"
            data-testid="founder-letter"
          >
            <p className="text-2xl md:text-3xl font-heading font-bold uppercase tracking-tight leading-tight text-white">
              Welcome to Faith Athletic Clothing.
            </p>

            <p className="text-base">
              We hope you enjoy the products — and the meaning behind wearing
              them.
            </p>

            <p className="text-base text-zinc-300">
              We see many faith-based clothing companies with a purpose for
              honoring God's word and moving in that direction — only to serve
              one demographic or faith-based belief circle.
            </p>

            <p className="text-base text-zinc-300">
              With our brand you are not just wearing a product. You are
              wearing the belief on the journey God has directed you through.
              Through tough times and challenges, we hold on to the belief of
              the mustard seed — as it appears in various books of the Bible,
              but as my favorite in{" "}
              <span className="text-white font-semibold">
                Matthew 17:20:
              </span>
            </p>

            <blockquote className="relative my-8 p-6 border-l-4 border-amber-300/70 bg-zinc-900/60">
              <span className="absolute -top-4 left-2 font-heading text-6xl text-amber-300/30 leading-none">
                "
              </span>
              <p className="italic text-lg md:text-xl text-white leading-relaxed font-heading font-normal">
                Truly I tell you, if you have faith as a mustard seed, you can
                say to this mountain, 'Move from here to there' — and it will
                move. Nothing will be impossible for you.
              </p>
              <footer className="text-[10px] tracking-[0.3em] uppercase text-amber-300/80 mt-3">
                — Matthew 17:20
              </footer>
            </blockquote>

            <p className="text-base text-zinc-300">
              As you walk with our Faith Athletic gear, hold those mustard
              seeds of faith — not only in your physical body, but knowing
              that the Holy Spirit drives that faith with you each day He
              breathes life into you.
            </p>

            <p className="text-base text-zinc-300 pt-2">Be blessed,</p>

            <div className="pt-2 pb-1">
              <div
                className="font-signature text-5xl md:text-6xl text-white leading-none"
                data-testid="founder-signature"
              >
                Michael Lewis
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-800 inline-block pr-12">
                <div className="font-bold text-sm uppercase tracking-wide">
                  Michael Lewis
                </div>
                <div className="text-[11px] text-zinc-500 tracking-widest uppercase">
                  Founder · Faith Athletic Clothing
                </div>
              </div>
            </div>

            <div className="pt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/#shop"
                className="bg-white text-black font-heading font-bold text-sm tracking-widest uppercase px-7 py-4 hover:bg-zinc-300 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="welcome-shop-cta"
              >
                Shop the Collection <ChevronRight size={16} />
              </Link>
              <Link
                to="/#culture"
                className="border border-zinc-700 text-white font-heading font-bold text-sm tracking-widest uppercase px-7 py-4 hover:bg-zinc-900 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="welcome-culture-cta"
              >
                Our Culture
              </Link>
            </div>
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Welcome;
